import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TEST_IDS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { toast } from "sonner";
import { Gavel, Plus, CheckCircle2, XCircle, Send, Rocket, ListPlus, X, Share2, Mail } from "lucide-react";
import { ResponsiveContainer, PieChart, Pie, Cell } from "recharts";

const STATUSES = ["Proposed", "Pending Approval", "Approved", "Rejected", "Implemented"];
const STATUS_STYLE = {
  Proposed: "bg-slate-100 text-slate-700",
  "Pending Approval": "bg-amber-100 text-amber-800",
  Approved: "bg-emerald-100 text-emerald-800",
  Rejected: "bg-rose-100 text-rose-800",
  Implemented: "bg-cyan-100 text-cyan-800",
};
const IMPACT_STYLE = {
  Low: "bg-slate-100 text-slate-600", Medium: "bg-sky-100 text-sky-700",
  High: "bg-orange-100 text-orange-700", Strategic: "bg-violet-100 text-violet-700",
};
// Allowed forward transitions in the lifecycle.
const NEXT = {
  Proposed: [["Pending Approval", "Submit for approval", Send]],
  "Pending Approval": [["Approved", "Approve", CheckCircle2], ["Rejected", "Reject", XCircle]],
  Approved: [["Implemented", "Mark implemented", Rocket]],
  Rejected: [["Pending Approval", "Re-open for approval", Send]],
  Implemented: [],
};

export default function Decisions() {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const [newText, setNewText] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ac/decisions");
      setDecisions(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function createDecision() {
    const decision_text = newText.trim();
    if (!decision_text) return;
    try {
      await api.post("/ac/decisions", { decision_text });
      setNewText("");
      toast.success("Decision added"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const counts = STATUSES.reduce((acc, s) => {
    acc[s] = (decisions || []).filter((d) => d.workflow_status === s).length; return acc;
  }, {});
  const visible = filter === "All" ? decisions : (decisions || []).filter((d) => d.workflow_status === filter);

  return (
    <div data-testid={TEST_IDS.decisionsRoot} className="p-6 md:p-8 lg:p-12">
      <header className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="eyebrow flex items-center gap-2"><Gavel className="w-3.5 h-3.5" /> Governance register</div>
          <h1 className="font-heading text-4xl sm:text-5xl mt-3 leading-none">Decisions.</h1>
        </div>
        <div className="flex items-center gap-2">
          <Input data-testid="new-decision-input" value={newText} onChange={(e) => setNewText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createDecision()} placeholder="Record a decision…" className="h-9 w-64" />
          <Button data-testid="new-decision-btn" onClick={createDecision} size="sm" className="h-9"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      </header>

      <DecisionsOverview counts={counts} total={(decisions || []).length} loading={loading} />

      <div className="flex items-center gap-2 flex-wrap mb-5">
        {["All", ...STATUSES].map((s) => (
          <button key={s} data-testid={`decision-filter-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setFilter(s)}
            className={`rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.1em] border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border text-muted-foreground"}`}>
            {s}{s !== "All" ? ` · ${counts[s] || 0}` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : visible.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-border p-12 text-center text-sm text-muted-foreground bg-card">
          No decisions here yet. They appear as meetings are processed, or add one above.
        </div>
      ) : (
        <div className="yl-surface overflow-hidden divide-y divide-border" data-testid="decision-register">
          {(visible || []).map((d) => (
            <button key={d.id} data-testid={`decision-row-${d.id}`} onClick={() => setActive(d)}
              className="w-full text-left p-5 flex items-start justify-between gap-4 hover:bg-secondary/40 transition-colors">
              <div className="min-w-0">
                <div className="font-medium">{d.decision_text}</div>
                <div className="text-xs text-muted-foreground mt-1 flex gap-3 flex-wrap">
                  <span>Owner: {d.decision_owner_name || "—"}</span>
                  {(d.approvals || []).length > 0 && <span>· {d.approvals.length} approval action(s)</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase ${IMPACT_STYLE[d.impact] || IMPACT_STYLE.Medium}`}>{d.impact}</span>
                <span className={`rounded-full px-2.5 py-1 text-[9px] font-semibold uppercase ${STATUS_STYLE[d.workflow_status]}`}>{d.workflow_status}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      <DecisionDrawer decision={active} onClose={() => setActive(null)} onChanged={load} setActive={setActive} />
    </div>
  );
}

function DecisionsOverview({ counts, total, loading }) {
  const palette = {
    Proposed: "#64748B", "Pending Approval": "#D9A441", Approved: "#2E9E6B",
    Rejected: "#B04A66", Implemented: "#29ABE2",
  };
  const data = STATUSES.map((s) => ({ name: s, value: counts[s] || 0 })).filter((d) => d.value > 0);
  const cells = data.length ? data : [{ name: "None", value: 1 }];
  const pending = (counts["Proposed"] || 0) + (counts["Pending Approval"] || 0);
  const cardCls = "yl-surface yl-hover-lift yl-beam p-5";
  return (
    <div data-testid="decisions-overview" className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
      <div className={cardCls}>
        <div className="eyebrow">Total decisions</div>
        <div className="font-heading text-4xl mt-2">{loading ? "—" : total}</div>
        <div className="text-xs text-muted-foreground mt-1">in the register</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow">Awaiting sign-off</div>
        <div className="font-heading text-4xl mt-2 text-amber-600">{loading ? "—" : pending}</div>
        <div className="text-xs text-muted-foreground mt-1">proposed or pending approval</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow">Implemented</div>
        <div className="font-heading text-4xl mt-2 text-cyan-600">{loading ? "—" : (counts["Implemented"] || 0)}</div>
        <div className="text-xs text-muted-foreground mt-1">{counts["Approved"] || 0} approved, not yet done</div>
      </div>
      <div className={cardCls}>
        <div className="eyebrow mb-1">By status</div>
        <div className="flex items-center gap-3">
          <div style={{ width: 90, height: 90, position: "relative" }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie data={cells} dataKey="value" innerRadius={28} outerRadius={44} paddingAngle={2} stroke="none">
                  {(cells || []).map((d) => <Cell key={d.name} fill={palette[d.name] || "#E2E8F0"} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", inset: 0 }} className="flex flex-col items-center justify-center">
              <div className="font-heading text-xl leading-none">{total}</div>
            </div>
          </div>
          <div className="flex-1 space-y-1">
            {(data || []).slice(0, 4).map((d) => (
              <div key={d.name} className="flex items-center justify-between text-[11px]">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: palette[d.name] || "#E2E8F0" }} />{d.name}</span>
                <span className="font-semibold">{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function DecisionDrawer({ decision, onClose, onChanged, setActive }) {
  const [reason, setReason] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  useEffect(() => { setReason(""); setFollowUp(""); setShareEmail(""); }, [decision]);
  if (!decision) return null;
  const d = decision;

  async function shareWithGuest() {
    const email = shareEmail.trim();
    if (!email || !email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSharing(true);
    try {
      const { data } = await api.post(`/ac/decisions/${d.id}/share`, { email });
      setShareEmail("");
      if (data.personal_inbox) {
        toast.warning(`Sent to ${email} — note this looks like a personal inbox. Anyone with the link can approve or reject.`);
      } else {
        toast.success(`Approval request sent to ${email}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSharing(false); }
  }

  async function transition(to) {
    if (to === "Rejected" && !reason.trim()) { toast.error("Please add a reason to reject"); return; }
    try {
      const { data } = await api.post(`/ac/decisions/${d.id}/transition`, { to, reason: reason.trim() || undefined });
      toast.success(`Decision ${to}`);
      setActive(data); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }
  async function addFollowUp() {
    const title = followUp.trim();
    if (!title) return;
    try {
      const { data } = await api.post(`/ac/decisions/${d.id}/follow-up-tasks`, { items: [{ title }] });
      setFollowUp(""); toast.success("Follow-up task created");
      setActive({ ...d, follow_up_task_ids: [...(d.follow_up_task_ids || []), ...(data.task_ids || [])] });
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <Sheet open={!!decision} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="decision-drawer" className="w-full sm:max-w-xl overflow-y-auto">
        <SheetHeader><SheetTitle className="text-left pr-6">{d.decision_text}</SheetTitle></SheetHeader>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${STATUS_STYLE[d.workflow_status]}`}>{d.workflow_status}</span>
          <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase ${IMPACT_STYLE[d.impact] || IMPACT_STYLE.Medium}`}>{d.impact} impact</span>
        </div>

        <div className="mt-4 text-sm text-muted-foreground space-y-1">
          <div>Owner: <span className="text-foreground font-medium">{d.decision_owner_name || "—"}</span></div>
          {d.context && <p className="pt-1"><span className="text-foreground font-medium">Context:</span> {d.context}</p>}
          {d.rationale && <p><span className="text-foreground font-medium">Rationale:</span> {d.rationale}</p>}
          {d.meeting_id && <Link to={`/meetings/${d.meeting_id}`} data-testid="decision-drawer-meeting-link" className="text-primary underline underline-offset-4 inline-block pt-1">View source meeting →</Link>}
        </div>

        {/* Workflow actions */}
        <div className="mt-6">
          <div className="eyebrow mb-2">Workflow</div>
          <div className="flex flex-wrap gap-2">
            {(NEXT[d.workflow_status] || []).map(([to, label, Icon]) => (
              <Button key={to} data-testid={`decision-action-${to.replace(/\s+/g, "-").toLowerCase()}`}
                size="sm" variant={to === "Rejected" ? "outline" : "default"} className="h-8" onClick={() => transition(to)}>
                <Icon className="w-4 h-4 mr-1" />{label}
              </Button>
            ))}
            {(NEXT[d.workflow_status] || []).length === 0 && <span className="text-sm text-muted-foreground">No further actions.</span>}
          </div>
          {d.workflow_status === "Pending Approval" && (
            <Input data-testid="decision-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required to reject)" className="h-8 text-sm mt-2" />
          )}
        </div>

        {/* Approval matrix */}
        {(d.approvals || []).length > 0 && (
          <div className="mt-6">
            <div className="eyebrow mb-2">Approval history</div>
            <div className="space-y-1.5">
              {(d.approvals || []).map((a, i) => (
                <div key={i} data-testid={`decision-approval-${i}`} className="text-xs flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase ${STATUS_STYLE[a.action]}`}>{a.action}</span>
                  <span className="text-foreground">{a.by_name}</span>
                  {a.reason && <span className="text-muted-foreground">— {a.reason}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Follow-up tasks */}
        <div className="mt-6 mb-10">
          <div className="eyebrow mb-2 flex items-center gap-1.5"><ListPlus className="w-3.5 h-3.5" /> Follow-up tasks</div>
          <div className="flex gap-2">
            <Input data-testid="decision-followup-input" value={followUp} onChange={(e) => setFollowUp(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addFollowUp()} placeholder="Create a follow-up task…" className="h-9 text-sm" />
            <Button data-testid="decision-followup-btn" size="sm" className="h-9" onClick={addFollowUp}><Plus className="w-4 h-4" /></Button>
          </div>
          {(d.follow_up_task_ids || []).length > 0 && (
            <div className="text-xs text-muted-foreground mt-2">{d.follow_up_task_ids.length} follow-up task(s) created from this decision.</div>
          )}
        </div>

        {/* Share with external guest */}
        <div className="mt-2 mb-12 rounded-2xl border border-border bg-secondary/40 p-4">
          <div className="eyebrow mb-1 flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5" /> Share with a client</div>
          <p className="text-xs text-muted-foreground mb-2">Email anyone — no MeetIQ account needed. They can approve or reject straight from their inbox.</p>
          <div className="flex gap-2">
            <Input data-testid="decision-share-input" type="email" value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && shareWithGuest()}
              placeholder="client@company.com" className="h-9 text-sm" />
            <Button data-testid="decision-share-btn" size="sm" className="h-9" disabled={sharing} onClick={shareWithGuest}>
              <Mail className="w-4 h-4 mr-1" />{sharing ? "Sending…" : "Send"}
            </Button>
          </div>
          {(d.guest_shares || []).length > 0 && (
            <div className="text-[11px] text-muted-foreground mt-2">Shared with {(d.guest_shares || []).map((g) => g.email).join(", ")}</div>
          )}
        </div>

        <button data-testid="decision-drawer-close" onClick={onClose} className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
      </SheetContent>
    </Sheet>
  );
}
